import { refreshIcons } from '../../base/utils.js';
import { closeAllDropdowns } from './shared.js';

export function initCustomSelect() {
    const selector = '.select-search, .form-select';

    document.querySelectorAll(selector).forEach(function (select) {
        if (select.dataset.initialized) return;
        select.dataset.initialized = 'true';

        const isMultiple = select.hasAttribute('multiple');
        const isSearchable = select.classList.contains('select-search');
        const isDisabled = select.hasAttribute('disabled');

        select.style.display = 'none';

        const container = document.createElement('div');
        container.className = 'select-search-container';
        if (isDisabled) container.classList.add('disabled');
        select.parentNode.insertBefore(container, select);
        container.appendChild(select);

        const trigger = document.createElement('div');
        trigger.className = 'select-search-trigger';
        if (isDisabled) trigger.classList.add('disabled');

        if (select.classList.contains('form-select-sm')) trigger.classList.add('form-select-sm');
        if (select.classList.contains('form-select-lg')) trigger.classList.add('form-select-lg');

        if (select.classList.contains('is-invalid')) trigger.classList.add('is-invalid');
        if (select.classList.contains('is-valid')) trigger.classList.add('is-valid');

        const classObserver = new MutationObserver((mutations) => {
            mutations.forEach((mutation) => {
                if (mutation.attributeName === 'class') {
                    if (select.classList.contains('is-invalid')) trigger.classList.add('is-invalid');
                    else trigger.classList.remove('is-invalid');

                    if (select.classList.contains('is-valid')) trigger.classList.add('is-valid');
                    else trigger.classList.remove('is-valid');

                    if (select.hasAttribute('disabled')) {
                        trigger.classList.add('disabled');
                        container.classList.add('disabled');
                    } else {
                        trigger.classList.remove('disabled');
                        container.classList.remove('disabled');
                    }
                }
            });
        });
        classObserver.observe(select, { attributes: true });

        const updateTrigger = () => {
            trigger.innerHTML = '';

            if (isMultiple) {
                const selectedOptions = Array.from(select.options).filter(opt => opt.selected && !opt.disabled);

                if (selectedOptions.length === 0) {
                    const placeholder = select.querySelector('option[disabled][selected]')?.text
                        || select.getAttribute('placeholder')
                        || 'Pilih opsi...';
                    trigger.innerHTML = `<span class="select-search-placeholder">${placeholder}</span>`;
                } else {
                    trigger.classList.add('has-selection');
                    selectedOptions.forEach(opt => {
                        const chip = document.createElement('span');
                        chip.className = 'select-search-chip';
                        chip.innerHTML = `${opt.text} <span class="chip-remove"><i data-lucide="x" class="w-3 h-3"></i></span>`;

                        chip.querySelector('.chip-remove').addEventListener('click', (e) => {
                            e.stopPropagation();
                            opt.selected = false;
                            select.dispatchEvent(new Event('change'));
                        });
                        trigger.appendChild(chip);
                    });
                }
            } else {
                const selectedOption = select.options[select.selectedIndex];

                if (select.value === "" || (selectedOption && selectedOption.disabled)) {
                    trigger.innerHTML = `<span class="select-search-placeholder">${selectedOption?.text || select.getAttribute('placeholder') || 'Pilih...'}</span>`;
                } else {
                    trigger.textContent = selectedOption ? selectedOption.text : 'Pilih...';
                }
            }
            refreshIcons();
        };

        updateTrigger();

        const dropdown = document.createElement('div');
        dropdown.className = 'select-search-dropdown';

        let searchInput;
        if (isSearchable) {
            searchInput = document.createElement('input');
            searchInput.type = 'text';
            searchInput.className = 'select-search-input';
            searchInput.placeholder = 'Cari...';
            searchInput.addEventListener('click', (e) => e.stopPropagation());
            dropdown.appendChild(searchInput);
        }

        const optionsContainer = document.createElement('div');
        optionsContainer.className = 'select-search-options';
        dropdown.appendChild(optionsContainer);

        const emptyMessage = document.createElement('div');
        emptyMessage.className = 'select-search-empty';
        emptyMessage.textContent = 'Tidak ditemukan';
        dropdown.appendChild(emptyMessage);

        container.appendChild(trigger);
        container.appendChild(dropdown);

        const renderOptions = () => {
            optionsContainer.innerHTML = '';
            Array.from(select.options).forEach(function (option) {
                const optionEl = document.createElement('div');
                optionEl.className = 'select-search-option';
                if (option.selected) optionEl.classList.add('selected');
                if (option.disabled) optionEl.classList.add('disabled');

                optionEl.textContent = option.text;
                optionEl.dataset.value = option.value;

                optionEl.addEventListener('click', function (e) {
                    if (option.disabled) return;

                    if (isMultiple) {
                        e.stopPropagation();

                        const newState = !option.selected;
                        option.selected = newState;

                        if (newState) {
                            optionEl.classList.add('selected');
                        } else {
                            optionEl.classList.remove('selected');
                        }

                        select.dispatchEvent(new Event('change'));
                        updateTrigger();

                        if (searchInput) {
                            searchInput.focus();
                        }
                    } else {
                        select.value = option.value;
                        updateTrigger();

                        optionsContainer.querySelectorAll('.select-search-option').forEach(o => o.classList.remove('selected'));
                        optionEl.classList.add('selected');

                        closeAllDropdowns();
                        select.dispatchEvent(new Event('change'));
                    }
                });

                optionsContainer.appendChild(optionEl);
            });
        };

        const refreshDropdownState = () => {
            const query = searchInput ? searchInput.value.toLowerCase() : '';
            let visibleCount = 0;
            const optionEls = optionsContainer.querySelectorAll('.select-search-option');

            Array.from(select.options).forEach((opt, idx) => {
                const el = optionEls[idx];
                if (!el) return;

                if (opt.selected) el.classList.add('selected');
                else el.classList.remove('selected');

                const matchesSearch = opt.text.toLowerCase().includes(query);
                const isHiddenSelection = isMultiple && opt.selected;

                if (matchesSearch && !isHiddenSelection) {
                    el.classList.remove('hidden');
                    el.classList.remove('selected-hidden');
                    visibleCount++;
                } else {
                    el.classList.add('hidden');
                    if (isHiddenSelection) el.classList.add('selected-hidden');
                }
            });

            if (visibleCount === 0) emptyMessage.classList.add('active');
            else emptyMessage.classList.remove('active');
        };

        renderOptions();

        if (searchInput) {
            searchInput.addEventListener('input', refreshDropdownState);
        }

        trigger.addEventListener('click', function (e) {
            if (isDisabled) return;
            e.stopPropagation();

            const isActive = dropdown.classList.contains('active');
            closeAllDropdowns();

            if (!isActive) {
                const rect = trigger.getBoundingClientRect();
                const spaceBelow = window.innerHeight - rect.bottom;
                const minSpaceRequired = 260;

                dropdown.classList.remove('open-upwards');

                if (spaceBelow < minSpaceRequired) {
                    dropdown.classList.add('open-upwards');
                }

                dropdown.classList.add('active');
                if (searchInput) {
                    searchInput.value = '';
                    searchInput.dispatchEvent(new Event('input'));
                    setTimeout(() => searchInput.focus(), 50);
                }
            }
        });

        select.addEventListener('change', function () {
            updateTrigger();
            refreshDropdownState();
        });
    });

    document.addEventListener('click', function (e) {
        if (!e.target.closest('.select-search-container')) {
            closeAllDropdowns();
        }
    });
}
